/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/
import React from 'react';
import {useSelector} from 'react-redux';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import {makeStyles} from '@material-ui/core/styles';
import ExpandMore from '@material-ui/icons/ExpandMore';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {
  AugmentedSmarterTrailerSensorProperty,
  EntityStatus,
  SensorUnits,
  VehicleWithTrailerTires
} from '@ion/core/src/types/entities';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {ExpandableListItem} from '@ion/dls/src/components/lists/ExpandableListItem';
import {MaterialExpandMoreIcon} from '@ion/dls/src/icons/MaterialExpandMoreIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme, IconSize} from '@ion/dls/src/types/types';
import {dateTimeFormatter} from '@ion/dls/src/utils/ComponentUtils';
import SensorListItem from './SensorListItem';
import SensorListItemExpandedContent from './SensorListItemExpandedContent';
import {getImperialMeasures, getSensorBatteryPercentageVolts} from './SmarterTrailerUtils';

export interface TireDataOverviewProps {
  trailerData: AugmentedSmarterTrailerSensorProperty;
  onToggleExpand?: (expanded: boolean) => void;
  selectedTire?: VehicleWithTrailerTires;
  hoveredTire?: VehicleWithTrailerTires;
  listItemsOpen?: VehicleWithTrailerTires;
  onItemClick?: (val: VehicleWithTrailerTires, open: boolean) => void;
  onHighlight?: Function;
}

// the current theme's side padding of the header, each row and its expanded
// details - so all three line up, edge to edge within the row ...
const CURRENT_ROW_INLINE_PADDING = 16;

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        element: {
          smallExpansion: {
            close: expansionClose,
            open: expansionOpen
          }
        }
      }
    }
  } = theme;
  const contentStyles = getAssetDetailsContentStyles();
  return {
    alerts : {
      ...dlsTypography('panelCards'),
      color : dlsColor('grey2')
    },
    issueListItem : {

    },
    issueName : {
      color : dlsColor('caRed')
    },
    unitDisplay : {
      textTransform : 'uppercase',
      color         : ({color}: {color: string}) => color,
      letterSpacing : 'normal'
    },
    tireOverviewListWrapper : {
      [theme.breakpoints.down('sm')] : {
        display        : 'flex',
        justifyContent : 'center',
        alignItems     : 'center'
      }
    },
    tireOverviewList : {
      border                         : `solid 1px ${dlsColor('grey5')}`,
      paddingBottom                  : '0px',
      width                          : '100%',
      [theme.breakpoints.down('sm')] : {
        minWidth : 300
      }
    },
    headerListItem : {
      ...dlsTypography('chipButton')
    },
    tireOverviewListItem : {
      ...dlsTypography('body3'),
      borderTop : `solid 1px ${dlsColor('grey5')}`,
      height    : '40px'
    },
    itemOpen : {
      borderBottom : `solid 1px ${dlsColor('grey5')}`
    },
    listItem : {
      paddingLeft  : '12px',
      paddingRight : '12px',
      '&:hover'    : {
        backgroundColor : dlsColor('paleBlue2'),
        border          : `solid 1px ${dlsColor('aaBlue')}`
      }
    },
    wetsItem : {
      backgroundColor : dlsColor('grey6')
    },
    selectedItem : {
      backgroundColor : dlsColor('paleBlue2'),
      border          : `solid 1px ${dlsColor('aaBlue')}`
    },
    expandIcon : {
      color          : dlsColor('grey3'),
      width          : '100%',
      display        : 'flex',
      justifyContent : 'flex-end'
    },
    closed : {
      transform  : 'rotateZ(180deg)',
      transition : `transform ${expansionClose}`
    },
    open : {
      transform  : 'rotateZ(0deg)',
      transition : `transform ${expansionOpen}`
    },
    // current theme only - the bordered list becomes a stack of bordered
    // tile rows (8px gap), matching the Asset Details Drawer's other
    // sections, instead of the legacy single-bordered, underlined list ...
    currentTireOverviewList : {
      border        : 'none',
      display       : 'flex',
      flexDirection : 'column',
      gap           : '8px'
    },
    currentHeaderRow : {
      height  : 'auto',
      border  : 'none !important',
      padding : `0 ${CURRENT_ROW_INLINE_PADDING}px`
    },
    currentListRow : {
      ...contentStyles.tile,
      paddingLeft     : `${CURRENT_ROW_INLINE_PADDING}px !important`,
      paddingRight    : `${CURRENT_ROW_INLINE_PADDING}px !important`,
      height          : 'auto',
      border          : `1px solid ${dlsColor('grey7')} !important`,
      backgroundColor : `${dlsColor('white')} !important`
    },
    // current theme only - the expanded details sit flush under their row,
    // without the selected row's tinted padding or side borders ...
    currentExpandedWrapper : {
      backgroundColor : 'transparent !important'
    },
    currentExpandedItem : {
      padding         : `0 ${CURRENT_ROW_INLINE_PADDING}px !important`,
      border          : 'none !important',
      backgroundColor : 'transparent !important',
      '&:hover'       : {
        backgroundColor : 'transparent !important'
      }
    },
    // a flex box so the open/closed rotation applies to the arrow ...
    currentArrow : {
      display : 'flex'
    },
    currentSelectedItem : {
      border : `1px solid ${dlsColor('aaBlue')} !important`
    },
    currentUnitDisplay : {
      ...contentStyles.unit,
      textTransform : 'uppercase',
      color         : ({color}: {color: string}) => color,
      letterSpacing : 'normal'
    }
  };
});

export const deviceSignalMapping = {
  [EntityStatus.Enabled]  : 'common:deviceStatus.connected',
  [EntityStatus.Disabled] : 'common:deviceStatus.disconnected'
};

type TireOverviewStyles = Record<string, string>;

type TireRowState = {
  itemOpen: boolean;
  isHighlighted: boolean;
};

/**
 *  Resolves the Tire Data Overview's current/legacy class names once per
 *  render - the bordered current-theme tile rows (with flush, border and
 *  tint-free expanded details) or the legacy single-bordered list - plus
 *  resolvers for the per-row and per-arrow class names that depend on each
 *  row's open/highlighted state.
 */
const getTireOverviewClasses = (classes: TireOverviewStyles, isCurrent: boolean) => ({
  listClassName   : themedClassName(classes.tireOverviewList, classes.currentTireOverviewList, isCurrent),
  headerClassName : classnames(
    classes.tireOverviewListItem,
    classes.headerListItem,
    isCurrent && classes.currentHeaderRow
  ),
  expandedWrapperClassName : isCurrent ? classes.currentExpandedWrapper : undefined,
  expandedItemClassName    : isCurrent ? classes.currentExpandedItem : undefined,
  getRowClassName          : ({itemOpen, isHighlighted}: TireRowState): string => classnames(
    classes.tireOverviewListItem,
    classes.listItem,
    isHighlighted && classes.selectedItem,
    itemOpen && classes.itemOpen,
    isCurrent && classes.currentListRow,
    isCurrent && isHighlighted && classes.currentSelectedItem
  ),
  // the current theme's arrow points down while open, the legacy one up ...
  getArrowClassName : (itemOpen: boolean): string => (
    isCurrent
      ? classnames(classes.currentArrow, itemOpen ? classes.open : classes.closed)
      : (itemOpen ? classes.closed : classes.open)
  )
});

type TireRowArrowProps = {
  className: string;
  isCurrent: boolean;
};

/**
 *  Renders a Tire Data Overview list row's expand/collapse arrow - the
 *  current theme's accordion arrow or the legacy `ExpandMore` icon, rotated
 *  by the resolved open/closed `className`.
 */
const TireRowArrow = ({className, isCurrent}: TireRowArrowProps) => (
  isCurrent ? (
    <span className={className}>
      <MaterialExpandMoreIcon size={IconSize.small} color={dlsColor('grey2')} />
    </span>
  ) : (
    <ExpandMore className={className} />
  )
);

/**
 *  Simple abstraction so we can styling to the unit displays
 *
 * ```typescript
 *  import {UppercaseUnitDisplay} from '@ion/features/src/smarterTrailer';
 *
 *  <UppercaseUnitDisplay value='35 PSI'
 *    colo={dlsColor('grey4)} />
 * ```
 */
export const UppercaseUnitDisplay = ({value, color = dlsColor('grey3')}: {value: string; color?: string;}) => {
  const classes      = useStyles({color});
  const {isCurrent}  = useThemeContext();
  return (
    <span className={themedClassName(classes.unitDisplay, classes.currentUnitDisplay, isCurrent)}>
      {value}
    </span>
  );
};

/**
 *  This is the data section of the tire section of the asset detail panel.
 *  This component is meant to sync with the trailer icon and handle displaying
 *  the data list from the augmented trailer data while passing open and selected states
 *  between this list and the icon.
 *
 * ```typescript
 *  import {TireDataOverview} from '@ion/features/src/smarterTrailer';
 *
 *  <TireDataOverview trailerData={trailerData}
 *    selectedTire='trailerBackLeftInside'
 *    listItemsOpen={[
 *      'trailerBackLeftInside',
 *      'trailerFrontLeftOutside',
 *      'trailerBackRightInside'
 *    ]} onItemClick={onItemClick} />
 * ```
 */
export const TireDataOverview = ({
  trailerData,
  selectedTire,
  hoveredTire,
  listItemsOpen,
  onHighlight,
  onItemClick
}: TireDataOverviewProps) => {
  const classes                    = useStyles();
  const {isCurrent}                = useThemeContext();
  const userPreferredUnitOfMeasure = useSelector(({login: {user}}) => user.unitOfMeasure);
  const {
    listClassName,
    headerClassName,
    expandedWrapperClassName,
    expandedItemClassName,
    getRowClassName,
    getArrowClassName
  } = getTireOverviewClasses(classes, isCurrent);

  return (
    <div className={classes.tireOverviewListWrapper}>
      <List className={listClassName}>
        <ListItem className={headerClassName}>
          <SensorListItem header
            trailingIconColumn
            columnContent={[
              translate('common:smarterTrailer.tires.name'),
              translate('common:smarterTrailer.tires.temperature.shorterLabel'),
              translate('common:smarterTrailer.tires.pressure.shorterLabel'),
              ''
            ]} />
        </ListItem>
        {
          Object.values(trailerData).map((wheelData, i) => {
            const {
              batteryVoltage,
              device : {
                status: deviceStatus
              },
              messageTime,
              sensorId,
              wheelLocation,
              sensorTag,
              wheelDisplayName,
              temperature: {
                value : tempValue,
                units : tempUnit
              } = {},
              lowPressure,
              highPressure,
              highTemperature,
              lowPressureAlarm,
              highPressureAlarm,
              temperatureAlarm,
              pressure : {
                value : pressureValue,
                units : pressureUnit
              } = {}
            }                       = wheelData;
            const batteryValue      = batteryVoltage?.value;
            const batteryUnits      = batteryVoltage?.units;
            const isWets            = sensorTag === 'WD1';
            const hasTempIssues     = Boolean(highTemperature || temperatureAlarm);
            const hasPressureIssues = Boolean(
              lowPressure ||
              highPressure ||
              lowPressureAlarm ||
              highPressureAlarm
            );
            const itemOpen          = listItemsOpen === wheelLocation;
            const itemSelected      = selectedTire === wheelLocation;
            const isHighlighted     = hoveredTire === wheelLocation || itemSelected;

            return (
              <div key={`tire-grid-item-${i}`} id={`tire-grid-${wheelLocation}`}>
                <ExpandableListItem highlightSelected
                  itemId={wheelLocation}
                  onItemHover={onHighlight}
                  itemOpen={itemOpen}
                  itemSelected={itemSelected}
                  setItemOpen={onItemClick}
                  expandedWrapperClass={expandedWrapperClassName}
                  expandedItemClass={expandedItemClassName}
                  listClass={getRowClassName({itemOpen, isHighlighted})}
                  listItem={
                    <SensorListItem trailingIconColumn
                      columnContent={[
                        wheelDisplayName,
                        <UppercaseUnitDisplay color={hasTempIssues ? dlsColor('caRed') : dlsColor('grey3')}
                          value={!parseFloat(tempValue)
                            ? translate('common:notAvailable')
                            : `${getImperialMeasures(tempValue, tempUnit, userPreferredUnitOfMeasure)}`} />,
                        <UppercaseUnitDisplay color={hasPressureIssues ? dlsColor('caRed') : dlsColor('grey3')}
                          value={isWets || !parseFloat(pressureValue)
                            ? translate('common:notAvailable')
                            : `${getImperialMeasures(pressureValue, pressureUnit, userPreferredUnitOfMeasure)}`} />,
                        <span className={classes.expandIcon}>
                          <TireRowArrow className={getArrowClassName(itemOpen)}
                            isCurrent={isCurrent} />
                        </span>
                      ]} />
                  } expandedItem={
                    <SensorListItemExpandedContent itemSelected={itemSelected}
                      signal={translate(deviceSignalMapping[deviceStatus])}
                      timeStamp={dateTimeFormatter(messageTime)}
                      location={translate(`common:smarterTrailer.tires.locations.${wheelLocation}`)}
                      batteryLevel={getSensorBatteryPercentageVolts(batteryValue)}
                      batteryReading={batteryValue ? `${batteryValue} ${batteryUnits}` : translate('common:notAvailable')}
                      batteryUnits={batteryUnits as SensorUnits || ''}
                      deviceId={sensorId} />
                  } />
              </div>
            );
          })
        }
      </List>
    </div>
  );
};

export default TireDataOverview;
